import {PopupDropdown} from '@wandb/weave/common/components/PopupDropdown';
import {TEAL_450, TEAL_550} from '@wandb/weave/common/css/globals.styles';
import {markdownToText} from '@wandb/weave/common/util/markdown';
import {Button} from '@wandb/weave/components/Button';
import {IconDelete} from '@wandb/weave/components/Icon';
import React, {useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import {useUpdateArtifactCollectionMutation} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {fromNow} from '../../../util/date';
import {maybePluralize} from '../../../util/uihelpers';
import {job as jobUrl, jobs as jobsUrl} from '../../../util/urls';
import {BackLink} from '../../BackLink';
import {Chip} from '../Chip';
import {DeleteJobDialog} from '../Jobs/DeleteJobDialog';
import {EditableName} from './EditableName';
import * as S from './JobDetailHeader.styles';
import {JobDetails} from './utilTypes';

type JobDetailHeaderProps = {
  entityName: string;
  projectName: string;
  job: JobDetails;
  onLaunchClick: () => void;
  onDescriptionMoreClick: () => void;
};

const MoreLink = styled(Link)`
  color: ${TEAL_550};
  font-weight: 600;
  &:hover {
    color: ${TEAL_450};
  }
`;
MoreLink.displayName = 'S.MoreLink';

export const JobDetailHeader = ({
  entityName,
  projectName,
  job,
  onLaunchClick,
  onDescriptionMoreClick,
}: JobDetailHeaderProps) => {
  const history = useHistory();

  const numVersions = job.versions.length;
  const versionLabel = maybePluralize(numVersions, 'version', 's');
  let timeChip = null;
  if (job.runs.length > 0) {
    // Job runs are sorted by recency.
    const mostRecentRunTimestamp = job.runs[0].createdAt;
    const then = new Date(mostRecentRunTimestamp);
    const relative = fromNow(then);
    const tooltip = `The last run of this job was ${relative}`;
    timeChip = <Chip icon="run" label={relative} tooltip={tooltip} />;
  }

  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
  const [deletingJobName, setDeletingJobName] = useState<string | null>(null);
  const closeDialog = () => {
    setDeletingJobId(null);
  };

  const menuOptions = [
    {
      key: 'delete-job',
      text: 'Delete Job',
      icon: <IconDelete style={{marginRight: '4px'}} />,
      onClick: () => {
        Analytics.track('clicked delete job', {
          entityName,
          jobId: job.id,
          location: 'job detail header',
        });
        setDeletingJobId(job.id);
        setDeletingJobName(job.name);
      },
    },
  ];

  const [updateArtifactCollection] = useUpdateArtifactCollectionMutation();

  const onRenameJob = (newName: string) => {
    updateArtifactCollection({
      variables: {
        artifactSequenceID: job.id,
        name: newName,
      },
    });
    Analytics.track('edit job name', {
      jobId: job?.id,
      oldName: job?.name,
      newName,
      entityName,
      location: 'job detail header',
    });
  };

  const onDeleteJob = () => {
    const url = jobsUrl({entityName, projectName});
    history.push(url);
  };

  // TODO: What do we want the max line length to be?
  // TODO: Smarter truncation of description string (avoid midword)
  const {description} = job;
  let renderedDesc = <div>{description}</div>;
  if (description) {
    const trimmed = description.trim();
    const textContent = markdownToText(trimmed);
    const firstLineDescription = textContent.split('\n', 1)[0];
    const truncatedDescription = firstLineDescription.slice(0, 180);
    if (truncatedDescription !== trimmed) {
      const urlParams = {
        entityName,
        projectName,
        jobId: job.id,
        jobVersion: job.versionInfo.runTarget,
        tab: 'job_details',
      };
      const urlMetadata = jobUrl(urlParams);
      renderedDesc = (
        <div>
          {truncatedDescription}…{' '}
          <MoreLink to={urlMetadata} onClick={onDescriptionMoreClick}>
            More
          </MoreLink>
        </div>
      );
    }
  }

  return (
    <S.JobDetailHeader>
      <DeleteJobDialog
        jobId={deletingJobId}
        jobName={deletingJobName}
        onClose={closeDialog}
        onDelete={onDeleteJob}
      />
      <S.TopAlignFlex>
        <S.CenteredFlex>
          <div>
            <BackLink to={jobsUrl({entityName, projectName})} text="Jobs" />
            <S.CenteredFlex>
              <EditableName content={job.name} onRename={onRenameJob} />
              <div>
                <Chip icon="flash-bolt" label="Job" />
                <Chip icon="versions-layers" label={versionLabel} />
                {timeChip}
              </div>
            </S.CenteredFlex>
          </div>
        </S.CenteredFlex>
        <S.RighthandPanel>
          <PopupDropdown
            position="bottom right"
            trigger={
              <Button
                icon="overflow-horizontal"
                size="large"
                variant="ghost"
                style={{marginRight: 8}}
              />
            }
            options={menuOptions}
          />

          <Button
            data-test="launch-job-button"
            size="large"
            variant="primary"
            icon="rocket-launch"
            disabled={numVersions === 0}
            onClick={onLaunchClick}>
            Launch
          </Button>
        </S.RighthandPanel>
      </S.TopAlignFlex>
      {renderedDesc}
    </S.JobDetailHeader>
  );
};
